import React from 'react';
import './AppPage.css';

interface ListIdentityProps {
  /** Object name or document number; one line, cut with an ellipsis and kept whole in `title`. */
  name: React.ReactNode;
  /** Opens the object's card; the name renders as a button when set. */
  onOpen?: () => void;
  /** Status, category or kind tags beside the name. */
  tags?: React.ReactNode;
  /** Muted 12 px facts joined with „ · ” on the second line; empty entries are skipped. */
  meta?: React.ReactNode[];
  /** Extra muted one-line notes below the meta (e.g. a storno note). */
  notes?: React.ReactNode[];
}

const isPresent = (value: React.ReactNode) => value !== null && value !== undefined && value !== false && value !== '';

/** Plain text of a node for the `title` tooltip of a line cut by the ellipsis. */
export const listTextOf = (node: React.ReactNode): string | undefined => {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    const text = node.map(listTextOf).filter(Boolean).join(' ');
    return text || undefined;
  }
  if (React.isValidElement<{ children?: React.ReactNode; title?: unknown }>(node)) {
    // A wrapper may carry the readable text itself (e.g. a category tag component).
    if (typeof node.props.title === 'string') return node.props.title;
    return listTextOf(node.props.children);
  }
  return undefined;
};

/**
 * Identity cell of a list table (components/layout/DESIGN.md, „Identity cell”;
 * direction 2026-10-19): line 1 the clickable name with its tags,
 * line 2 muted metadata. Every line stays on one line; the full text is in `title`.
 */
const ListIdentity: React.FC<ListIdentityProps> = ({ name, onOpen, tags, meta = [], notes = [] }) => {
  const facts = meta.filter(isPresent);
  const extraLines = notes.filter(isPresent);
  const nameText = listTextOf(name);
  const nameNode = <span className="app-page__identity-name" title={nameText}>{name}</span>;
  const metaText = facts.map(listTextOf).filter(Boolean).join(' · ');

  return (
    <div className="app-page__identity">
      <div className="app-page__identity-copy">
        <div className="app-page__identity-line">
          {onOpen ? (
            <button
              type="button"
              className="app-page__identity-open"
              onClick={(event) => {
                event.stopPropagation();
                onOpen();
              }}
            >
              {nameNode}
            </button>
          ) : nameNode}
          {isPresent(tags) ? <span className="app-page__identity-tags">{tags}</span> : null}
        </div>
        {facts.length > 0 ? (
          <span className="app-page__identity-meta" title={metaText || undefined}>
            {facts.map((fact, index) => (
              <React.Fragment key={index}>
                {index > 0 ? <span className="app-page__identity-sep" aria-hidden="true">{'·'}</span> : null}
                <span className="app-page__identity-fact">{fact}</span>
              </React.Fragment>
            ))}
          </span>
        ) : null}
        {extraLines.map((line, index) => (
          <span key={index} className="app-page__identity-meta" title={listTextOf(line)}>{line}</span>
        ))}
      </div>
    </div>
  );
};

export default ListIdentity;
